/*
 * 路由。
 *
 * history 模式 + 服务端 fallback：每条路由都要能被直接访问和刷新，
 * 因为网页会被分享出去。部署时 nginx / CDN 需把未命中的路径回落到 index.html。
 *
 * 不做旧版的占位 `/home`，也不做底部 TabBar——聊天是首页，其余能力是从
 * 对话里长出来的分支，不是并列的五个板块。
 *
 * `*` 兜底必须有：旧版定义了 /404 页面却没有通配路由，那个页面永远访问不到。
 *
 * `/login` 挂在 AppShell 之外：登录页不需要舞台、HUD 与右侧图标栏，
 * 套进去只会先渲染一整套登录后才有意义的外壳再盖住它。
 */

import { createBrowserRouter, Link, Navigate } from "react-router-dom";
import { AppShell, RequireSession } from "./App";
import { PageShell } from "./components/ui";
import { LoginPage } from "./features/auth/LoginPage";
import { ChatPage } from "./features/chat/ChatPage";
import { InformationAccess, InformationDetailPage, InformationPage } from "./features/information/InformationPage";
import { HistoryPage } from "./features/chat/HistoryPage";
import { ImageGalleryPage } from "./features/chat/ImageGalleryPage";
import { FavoritesPage } from "./features/favorites/FavoritesPage";
import { MemoriesPage } from "./features/memory/MemoriesPage";
import { DayPage } from "./features/memory/DayPage";
import { ConversationMemoryPage } from "./features/memory/ConversationMemoryPage";
import { ProfilePage } from "./features/profile/ProfilePage";
import { OrdersPage } from "./features/profile/OrdersPage";
import { RelationshipPage } from "./features/relationship/RelationshipPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { SkillExecutionPage } from "./features/skills/SkillExecutionPage";
import { useSession } from "./state/session";

function HomeEntry() {
  const session = useSession();
  return session.knowledgeQaHome ? (
    <Navigate to="/information" replace />
  ) : (
    <ChatPage />
  );
}

function NotFoundPage() {
  return (
    <PageShell title="这里没有路">
      <div className="prose">
        <p>
          梦境是个大迷宫，这个地址通向的地方不存在。
          可能是链接过期了，也可能是地址抄漏了一段。
          <Link to="/">回到和缈缈的对话</Link>。
        </p>
      </div>
    </PageShell>
  );
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/information", element: <InformationAccess><InformationPage /></InformationAccess> },
  { path: "/information/:id", element: <InformationAccess><InformationDetailPage /></InformationAccess> },
  { path: "/knowledge", element: <Navigate to="/information" replace /> },
  {
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: [
      { path: "/", element: <HomeEntry /> },
      { path: "/chat", element: <ChatPage /> },
      { path: "/history", element: <HistoryPage /> },
      { path: "/memories", element: <MemoriesPage /> },
      /*
       * 回忆点滴的第二、三层。层级体现在路径上，逐层能被直接访问与分享。
       *
       * ⚠ `:memoryId` 是**记忆碎片的 id，不是会话 id**：一次长对话按上下文
       * 折叠点切成多段，用户点开的是那一段。见 `services/me-api.ts` 的
       * `DayConversation`。
       */
      { path: "/memories/:date", element: <DayPage /> },
      { path: "/memories/:date/:memoryId", element: <ConversationMemoryPage /> },
      { path: "/favorites", element: <FavoritesPage /> },
      { path: "/images", element: <ImageGalleryPage /> },
      { path: "/relationship", element: <RelationshipPage /> },
      { path: "/profile", element: <ProfilePage /> },
      { path: "/orders", element: <OrdersPage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/skill/:executionId", element: <SkillExecutionPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
